import { useFrame } from "@react-three/fiber";
import { useMemo, useRef, type JSX } from "react";
import type { Group } from "three";

const CITY_DOTS: ReadonlyArray<{ readonly lat: number; readonly lon: number; readonly faction: string }> = [
  { lat: 54.78, lon: 32.05, faction: "USSR" },
  { lat: 49.84, lon: 24.03, faction: "USSR" },
  { lat: 50.11, lon: 8.68, faction: "WEHRMACHT" },
  { lat: 37.5, lon: 15.09, faction: "WEHRMACHT" },
  { lat: 35.47, lon: -97.52, faction: "US" },
  { lat: 32.36, lon: -86.3, faction: "US" },
  { lat: 53.41, lon: -2.99, faction: "UK" },
  { lat: 52.48, lon: -1.9, faction: "UK" },
];

const FACTION_COLOR: Record<string, string> = {
  USSR: "oklch(58% 0.19 25)",
  WEHRMACHT: "oklch(40% 0.04 250)",
  US: "oklch(50% 0.12 145)",
  UK: "oklch(46% 0.13 245)",
};

const SPHERE_RADIUS = 8;

function latLonToVec3(lat: number, lon: number, r: number): [number, number, number] {
  const phi = ((90 - lat) * Math.PI) / 180;
  const theta = ((lon + 180) * Math.PI) / 180;
  const x = -r * Math.sin(phi) * Math.cos(theta);
  const z = r * Math.sin(phi) * Math.sin(theta);
  const y = r * Math.cos(phi);
  return [x, y, z];
}

export function WorldScene(): JSX.Element {
  const root = useRef<Group>(null);

  useFrame((_, dt) => {
    if (root.current) root.current.rotation.y += dt * 0.04;
  });

  const dots = useMemo(
    () =>
      CITY_DOTS.map((c, i) => ({
        key: `${c.faction}-${i}`,
        pos: latLonToVec3(c.lat, c.lon, SPHERE_RADIUS + 0.05),
        color: FACTION_COLOR[c.faction] ?? "white",
      })),
    [],
  );

  return (
    <group ref={root} position={[0, 80, 0]}>
      <mesh>
        <sphereGeometry args={[SPHERE_RADIUS, 48, 32]} />
        <meshStandardMaterial
          color="oklch(28% 0.02 250)"
          roughness={0.9}
          metalness={0.1}
          emissive="oklch(14% 0.02 250)"
          emissiveIntensity={0.4}
        />
      </mesh>
      {dots.map((d) => (
        <mesh key={d.key} position={d.pos}>
          <sphereGeometry args={[0.12, 12, 12]} />
          <meshStandardMaterial
            color={d.color}
            emissive={d.color}
            emissiveIntensity={1.2}
            toneMapped={false}
          />
        </mesh>
      ))}
    </group>
  );
}
